/* eslint-disable jsx-a11y/aria-role */
import React, { useEffect, useMemo } from 'react';

import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { useQuery } from '@tanstack/react-query';
import { useServicerStore } from '../store';

import Layout from '../containers/Layout';
import PrivateRoute from '../containers/PrivateRoute';

// Auth
import LoginPage from '../pages/Auth/LoginPage';
import SignUpPage from '../pages/Auth/SignUpPage';
import AcceptInvitePage from '../pages/Auth/AcceptInvitePage';
import SetPasswordPage from '../pages/Auth/SetPasswordPage';
import QuickAuthPage from '../pages/Auth/QuickAuthPage';
import ForgotPasswordPage from '../pages/Auth/ForgotPasswordPage';

// Lenders
import AddOrEditLenderPage from '../pages/AddOrEditLenderPage';
import LendersPage from '../pages/LendersPage';

// Loans
import LoansPage from '../pages/LoansPage';
import AddOrEditLoanPage from '../pages/AddOrEditLoanPage';

// Admin
import HomePage from '../pages/Admin/HomePage';
import AddOrEditServicerPage from '../pages/Admin/AddOrEditServicerPage';

// Account
import AccountPage from '../pages/AccountsPage';

// Vendors
import VendorsPage from '../pages/Vendors/VendorsPage';
import AddOrEditVendorPage from '../pages/Vendors/AddOrEditVendorPage';

import { hasAuthData, clearAuthData } from '../helpers/authStorage';
import ThemeContextProvider from '../context/ThemeContext';
import UiContextProvider from '../context/UiContext';
import AuthService from '../services/AuthService';

// Not found page
import NotFoundPage from '../pages/NotFoundPage';

// Lender onboarding
import LenderOnboardingPage from '../pages/LenderOnboardingPage';
import LenderTermsPage from '../pages/LenderTermsPage';

// Dashboard
import DashboardPage from '../pages/DashboardPage';

// Bill payments
import BillPaymentsPage from '../pages/BillPayments/BillPaymentsPage';
import BillPaymentDetailsPage from '../pages/BillPayments/BillPaymentDetailsPage';
import AddBillPaymentPage from '../pages/BillPayments/AddBillPaymentPage';

// Payments
import PaymentsPage from '../pages/PaymentsPage';

// Bill.com Settlements
import BillComSettlementsPage from '../pages/BillComSettlementsPage';
import BillcomSettlementPage from '../pages/BillcomSettlementPage';

// Help
import ContactUsPage from '../pages/ContactUsPage';

// Borrower
import BorrowerSettingsPage from '../pages/BorrowerSettingsPage';

// Imports
import ImportsPage from '../pages/Imports/ImportsPage';
import ImportPage from '../pages/Imports/ImportPage';
import ImportDocumentPage from '../pages/Imports/ImportDocumentPage';

import getUserRole from '../helpers/getUserRole';
import useLenderData from '../hooks/useLenderData';

const ADMIN_HOSTNAME = 'admin.pixelservicing.com';
// const ADMIN_HOSTNAME = 'localhost';

const getDefaultRoute = (
  userRole,
  isAdminHostname,
  isLenderOnboarded,
  shouldAcceptTermsOfService
) => {
  if (!userRole) {
    return '/login';
  }

  if (userRole === 'superAdmin' && isAdminHostname) {
    return '/admin';
  }

  if (
    userRole === 'lender' &&
    shouldAcceptTermsOfService &&
    isLenderOnboarded
  ) {
    return '/lender_onboarding?terms=true';
  }

  if (userRole === 'lender' && !isLenderOnboarded) {
    return '/lender_onboarding';
  }

  if (userRole === 'superAdmin' || userRole === 'lender') {
    return '/dashboard';
  }

  if (userRole === 'borrower') {
    return '/loans';
  }

  return '/loans';
};

function RoutesComponent() {
  const setServicerId = useServicerStore((state) => state.setServicerId);
  const userRole = useServicerStore((state) => state.userRole);
  const setIsAdminHostname = useServicerStore(
    (state) => state.setIsAdminHostname
  );
  const setUserRole = useServicerStore((state) => state.setUserRole);
  const setUserId = useServicerStore((state) => state.setUserId);

  const { data: servicerId } = useQuery({
    queryKey: ['servicer_id'],
    queryFn: AuthService.getServicersId,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const isLoggedIn = hasAuthData();

  const { data: me, isFetching } = useQuery({
    queryKey: ['me'],
    queryFn: AuthService.getMe,
    enabled: isLoggedIn,
    retry: false,
  });

  // Set Sentry user context
  useEffect(() => {
    if (me) {
      Sentry.setUser({
        id: me.id,
        username: me.name,
        email: me.email,
      });
    }
  }, [me]);

  const { hostname } = window.location;

  const isAdminHostname = hostname === ADMIN_HOSTNAME;

  // Save isAdminHostname to the store
  useEffect(() => {
    setIsAdminHostname(isAdminHostname);
  }, [isAdminHostname, setIsAdminHostname]);

  // Save servicerId to the store
  useEffect(() => {
    if (servicerId) {
      setServicerId(servicerId.id);
    }
  }, [servicerId, setServicerId]);

  // Check and save user role and id
  useEffect(() => {
    if (me) {
      const role = getUserRole(me);
      setUserRole(role);
      setUserId(me.id);
      // Remove auth data if it is not valid and redirect to login page
    } else if (!isFetching && !me && isLoggedIn) {
      clearAuthData();
      window.location.href = '/login';
    }
  }, [me, setUserId, setUserRole, isFetching, isLoggedIn]);

  const { lenders, isReady } = useLenderData(me);

  const router = useMemo(() => {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route>
          <Route
            path="/loans"
            element={
              <PrivateRoute>
                <LoansPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/imports"
            element={
              <PrivateRoute>
                <ImportsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/imports/:id"
            element={
              <PrivateRoute>
                <ImportPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/imports/:importId/documents/:documentId"
            element={
              <PrivateRoute>
                <ImportDocumentPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/new"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditLoanPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/payments/:paymentId"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/invoices/:invoiceId"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit section="invoice" />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/documents/:documentId"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/participations/new"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/participations/:participationId"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/bills/:billId"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/bill-payments/:billPaymentId"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/loans/:id/:resource/:resourceId"
            element={
              <PrivateRoute>
                <AddOrEditLoanPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/lenders"
            element={
              <PrivateRoute role={['superAdmin', 'lender']}>
                <LendersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/lenders/new"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditLenderPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/lenders/:id"
            element={
              <PrivateRoute role={['superAdmin', 'lender']}>
                <AddOrEditLenderPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/lenders/:id/lien-holders/:lienHolderId"
            element={
              <PrivateRoute role={['superAdmin', 'lender']}>
                <AddOrEditLenderPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/lenders/:id/documents/:documentId"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditLenderPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/lenders/:id/distributions/:distributionId"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditLenderPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/lenders/:id/:resource/:resourceId"
            element={
              <PrivateRoute role={['superAdmin', 'lender']}>
                <AddOrEditLenderPage isEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute role="superAdmin">
                <Layout>
                  <h1>Empty</h1>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <PrivateRoute role="superAdmin">
                <AccountPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <PrivateRoute role="superAdmin">
                <VendorsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/vendors/new"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditVendorPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/vendors/:id"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditVendorPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/vendors/:id/contacts/:contactId"
            element={
              <PrivateRoute role="superAdmin">
                <AddOrEditVendorPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/bill-payments"
            element={
              <PrivateRoute role="superAdmin">
                <BillPaymentsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/bill-payments/new"
            element={
              <PrivateRoute role="superAdmin">
                <AddBillPaymentPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/bill-payments/:billPaymentId"
            element={
              <PrivateRoute role="superAdmin">
                <BillPaymentDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/lender_onboarding"
            element={
              <PrivateRoute role="lender">
                <LenderOnboardingPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute role={['superAdmin', 'borrower', 'lender']}>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute role="superAdmin">
                <PaymentsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/billcom-settlements"
            element={
              <PrivateRoute role="superAdmin">
                <BillComSettlementsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/billcom-settlements/:id"
            element={
              <PrivateRoute role="superAdmin">
                <BillcomSettlementPage />
              </PrivateRoute>
            }
          />
          {isAdminHostname && (
            <>
              <Route
                path="/admin"
                element={
                  <PrivateRoute role="superAdmin">
                    <HomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/servicers/new"
                element={
                  <PrivateRoute role="superAdmin">
                    <AddOrEditServicerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/servicers/:id"
                element={
                  <PrivateRoute role="superAdmin">
                    <AddOrEditServicerPage />
                  </PrivateRoute>
                }
              />
            </>
          )}
          <Route
            path="/contact-us"
            element={
              <PrivateRoute>
                <ContactUsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/borrower-settings"
            element={
              <PrivateRoute role="borrower">
                <BorrowerSettingsPage />
              </PrivateRoute>
            }
          />
          <Route path="/quick-auth" element={<QuickAuthPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<SetPasswordPage reset />} />
          <Route
            path="/lender_terms_of_service"
            element={<LenderTermsPage />}
          />

          <Route path="/not-found" element={<NotFoundPage />} />
          <Route
            path="*"
            element={
              <Navigate
                to={getDefaultRoute(
                  userRole,
                  isAdminHostname,
                  lenders?.every?.((lender) => lender?.onboarded === true),
                  lenders?.every?.(
                    (lender) => lender?.shouldAcceptTermsOfService === true
                  )
                )}
                replace
              />
            }
          />
        </Route>
      )
    );
  }, [isAdminHostname, lenders, userRole]);

  // Don't render the app until we check user role
  if ((!userRole && isLoggedIn) || !isReady) {
    return null;
  }

  return (
    <ThemeContextProvider>
      <UiContextProvider>
        <RouterProvider router={router} />
      </UiContextProvider>
    </ThemeContextProvider>
  );
}

export default RoutesComponent;
